import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { useAuth } from '../../context/AuthContext';
import { Bell, Clock, Trash2, CheckCircle } from 'lucide-react';
import api from '../../utils/api';

const Notifications = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNotifications = async () => {
      if (!user?.id) return;
      try {
        const response = await api(`/notifications`);
        if (response.ok) {
          const data = await response.json();
          setNotifications(data);
        }
      } catch (error) {
        console.error("Error fetching notifications:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [user?.id]);

  const markAsRead = async (id) => {
    try {
      await api(`/notifications/${id}/read`, { method: 'PATCH' });
      setNotifications(notifications.map(n => n._id === id ? { ...n, isRead: true } : n));
    } catch (error) {
      console.error("Error marking read:", error);
    }
  };

  const clearAll = async () => {
    if (!window.confirm("Clear all notifications?")) return;
    try {
      await api(`/notifications/user`, { method: 'DELETE' });
      setNotifications([]);
    } catch (error) {
      console.error("Error clearing notifications:", error);
    }
  };

  return (
    <MainLayout title="Notifications">
      <div style={{ maxWidth: '700px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '700' }}>Recent Activity</h2>
          {notifications.length > 0 && (
            <button 
              onClick={clearAll}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '6px', 
                fontSize: '13px', 
                color: '#ef4444', 
                background: 'none', 
                border: 'none', 
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              <Trash2 size={14} /> Clear All
            </button>
          )}
        </div>

        <div className="card" style={{ 
          background: 'white', 
          borderRadius: 'var(--border-radius-lg)',
          border: '1px solid var(--color-border-tertiary)',
          overflow: 'hidden'
        }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-tertiary)' }}>Loading...</div>
          ) : notifications.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {notifications.map((notif, idx) => (
                <div 
                  key={notif._id} 
                  onClick={() => !notif.isRead && markAsRead(notif._id)}
                  style={{
                    display: 'flex',
                    gap: '16px',
                    padding: '20px',
                    borderBottom: idx === notifications.length - 1 ? 'none' : '1px solid var(--color-border-tertiary)',
                    background: notif.isRead ? 'white' : '#f0f7ff',
                    transition: 'background 0.2s',
                    cursor: notif.isRead ? 'default' : 'pointer'
                  }}
                >
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: notif.isRead ? 'var(--color-background-secondary)' : 'var(--color-background-info)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    {notif.isRead ? <CheckCircle size={18} color="var(--color-text-tertiary)" /> : <Bell size={18} color="var(--color-brand-primary)" />}
                  </div>
                  <div style={{ flex: 1, position: 'relative' }}>
                    {!notif.isRead && (
                      <div style={{
                        position: 'absolute',
                        top: '-10px',
                        right: '-10px',
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: '#E24B4A'
                      }} />
                    )}
                    <div style={{ fontSize: '14px', fontWeight: notif.isRead ? '500' : '700', color: 'var(--color-text-primary)', marginBottom: '4px' }}>
                      {notif.title}
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--color-text-secondary)', lineHeight: '1.5' }}>
                      {notif.message}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'var(--color-text-tertiary)', marginTop: '8px' }}>
                      <Clock size={12} /> {new Date(notif.createdAt).toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ padding: '60px 20px', textAlign: 'center' }}>
              <Bell size={40} color="var(--color-border-primary)" style={{ marginBottom: '16px' }} />
              <div style={{ fontSize: '15px', color: 'var(--color-text-secondary)', fontWeight: '600' }}>All caught up!</div>
              <div style={{ fontSize: '13px', color: 'var(--color-text-tertiary)', marginTop: '4px' }}>You have no new notifications.</div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default Notifications;
